import type { OnInit } from '@angular/core'
import { Component, inject, signal } from '@angular/core'
import type { Event } from '@angular/router'
import { NavigationEnd, Router } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { filter } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { DialogService } from '../../services/dialog/dialog.service'
import type { SupportedLanguage } from '../../services/internationalization.service'
import { InternationalizationService } from '../../services/internationalization.service'
import { DesktopActionsComponent } from './components/desktop-actions/desktop-actions.component'
import { DesktopNavComponent } from './components/desktop-nav/desktop-nav.component'
import { LanguageModalComponent } from './components/language-modal/language-modal.component'
import { HeaderLogoComponent } from './components/logo/logo.component'
import { MobileMenuComponent } from './components/mobile-menu/mobile-menu.component'
import type { NavItem } from './interfaces/nav-item.interface'

@Component({
  selector: 'dhp-navigation-header',
  templateUrl: './navigation-header.component.html',
  styleUrls: ['./navigation-header.component.scss'],
  imports: [
    HeaderLogoComponent,
    DesktopNavComponent,
    DesktopActionsComponent,
    MobileMenuComponent,
    LanguageModalComponent,
    TranslatePipe,
  ],
})
export class NavigationHeaderComponent implements OnInit {
  private router = inject(Router)
  private translateService = inject(TranslateService)
  private i18nService = inject(InternationalizationService)
  dialogService = inject(DialogService)

  currentRoute = ''
  searchExpanded = false
  readonly currentLanguage = signal<SupportedLanguage>('nl')

  languageNavItem = {
    name: 'components.header.languageMenu',
    url: '#',
    dropdown: 'languageMenu',
    dropdownItems: [
      { name: 'languages.abbreviated.nl' },
      { name: 'languages.abbreviated.en' },
    ],
  }

  navItems: NavItem[] = [
    { name: 'components.header.library', url: '/' }, // Root URL for home
    // {
    //   name: 'components.header.products',
    //   dropdown: 'products',
    //   dropdownItems: [
    //     { name: 'components.header.magazines', url: 'magazines' },
    //     { name: 'components.header.books', url: 'books' },
    //     { name: 'components.header.contracts', url: 'contracts' },
    //   ],
    // },
    { name: 'components.header.editors', url: 'editors' },
    { name: 'components.header.authors', url: 'authors' },
    { name: 'components.header.prices', url: 'pricing' },
    {
      name: 'components.header.company',
      dropdown: 'company',
      dropdownItems: [
        { name: 'components.footer.aboutUs', url: '/about-us' },
        { name: 'components.footer.helpAndSupport', url: '/help' },
        // { name: 'components.footer.advertise', url: '/advertisement' },
        { name: 'components.footer.contact', url: '/contact' },
      ],
    },
    this.languageNavItem,
  ]

  constructor() {
    this.currentLanguage.set(
      this.translateService.getCurrentLang() as SupportedLanguage
    )

    this.translateService.onLangChange.subscribe(event => {
      this.currentLanguage.set(event.lang as SupportedLanguage)
    })

    if (!environment.production) {
      this.languageNavItem.dropdownItems.push({ name: 'QQX' })
    }
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => {
        this.dialogService.closeAll()
      })
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event: Event): event is NavigationEnd =>
            event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects.split('/')[1]
      })
  }

  // Navigation methods
  toggleMenu(): void {
    this.dialogService.toggle('menu')
  }

  closeMenu(): void {
    this.dialogService.toggle('menu', false)
  }

  onSearchStateChange(isExpanded: boolean): void {
    this.searchExpanded = isExpanded
  }

  selectLanguage(lang: SupportedLanguage): void {
    this.i18nService.setLanguagePreference(lang).subscribe({
      error: (err: unknown) => {
        console.error('Language change failed', err)
      },
    })

    if (lang !== 'nl' && lang !== 'en') {
      return
    }

    // Navigate to the equivalent page under the new language's prefix so the
    // URL and the language selector stay in sync.
    const bareUrl = this.router.url.replace(/^\/en(?=\/|\?|$)/, '') || '/'
    const targetUrl =
      lang === 'en' ? `/en${bareUrl === '/' ? '' : bareUrl}` : bareUrl
    // Pass a UrlTree, not the bare string: LocaleAwareRouter's
    // navigateByUrl(string) override would otherwise re-add the /en prefix
    // it just sees on the (still current) URL, undoing this exact switch
    // when moving from English back to Dutch.
    void this.router.navigateByUrl(this.router.parseUrl(targetUrl))
  }
}
